import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  onError?: (error: Error) => void;
}

interface State {
  hasError: boolean;
  error?: Error;
}

/**
 * Error boundary specifically for handling React DOM errors during PDF export
 * Prevents component crashes and allows export to proceed
 */
class ExportErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.warn('🛡️ ExportErrorBoundary caught error:', error.message);
    console.warn('Error details:', errorInfo);
    
    // Notify parent component of the error
    if (this.props.onError) {
      this.props.onError(error);
    }
  }

  render() {
    if (this.state.hasError) {
      // Return the children anyway to allow export to proceed
      // The error is likely a transient React DOM issue during export
      console.log('🛡️ Rendering children despite error to allow export to proceed');
      return this.props.children;
    }

    return this.props.children;
  }
}

export default ExportErrorBoundary;
